import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import get from 'lodash.get'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'
import ComponentScatter from '../../../../charts/echarts/ComponentScatter'

const useFormatters = (valueFormat, categoryFormat) => {
  const { formatter } = useFormattingContext()

  const xFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  const yFormatter = useCallback(d => formatter(d, categoryFormat), [formatter, categoryFormat])

  return { xFormatter, yFormatter }
}

function DenaliComponentScatter ({
  reportId,
  baseQuery,
  xAccessor,
  yAccessor,
  sizeAccessor,
  labelAccessor,
  symbolSize,
  sizeMin,
  sizeMax,
  xFormat,
  yFormat,
  palette,
  ...seriesProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(
    () => ({
      ...baseQuery,
      membershipFilter,
      filters: { ...(filter || {}), ...(baseQuery?.filters || {}) },
      reportId
    }),
    [reportId, baseQuery, filter, membershipFilter]
  )

  const { xFormatter, yFormatter } = useFormatters(xFormat, yFormat)
  const { data: report, isLoading } = useFirmReportData(query)

  const chartData = useMemo(() => {
    let minSize = Infinity
    let maxSize = -Infinity
    let sum = 0
    let sumSq = 0
    let count = 0

    const rawSeries = (report?.data || []).map(item => {
      const x = get(item, xAccessor)
      const y = get(item, yAccessor)
      let value
      if (sizeAccessor) {
        const size = Number(get(item, sizeAccessor))
        if (size < minSize) minSize = size
        if (size > maxSize) maxSize = size
        sum += size
        sumSq += size * size
        count += 1
        value = [x, y, size]
      } else {
        value = [x, y]
      }
      const label = labelAccessor
        ? String(get(item, labelAccessor))
        : undefined
      return { value, label }
    })

    const mean = sizeAccessor && count ? sum / count : 0
    const variance = sizeAccessor && count ? sumSq / count - mean * mean : 0
    const std = variance > 0 ? Math.sqrt(variance) : 0

    let series
    if (Array.isArray(palette) && palette.length) {
      series = rawSeries.map((d, i) => {
        const point = { value: d.value }
        if (labelAccessor && d.label != null) point.name = d.label
        point.itemStyle = { color: palette[i % palette.length] }
        return point
      })
    } else if (labelAccessor) {
      series = rawSeries.map(d => {
        const point = { value: d.value }
        if (d.label != null) point.name = d.label
        return point
      })
    } else {
      series = rawSeries.map(d => d.value)
    }

    return {
      series,
      sizeExtent: sizeAccessor ? [minSize, maxSize] : null,
      sizeStats: sizeAccessor ? { mean, std } : null
    }
  }, [report, xAccessor, yAccessor, sizeAccessor, labelAccessor, palette])

  const _symbolSize = useMemo(() => {
    if (symbolSize === 'linear') {
      if (Array.isArray(chartData?.sizeExtent)) {
        const [dataMin, dataMax] = chartData.sizeExtent
        const domain = dataMax - dataMin
        return val => {
          const raw = val[2]
          if (domain === 0) return sizeMin
          const t = (raw - dataMin) / domain
          return sizeMin + t * (sizeMax - sizeMin)
        }
      }
      return () => sizeMin
    }

    if (symbolSize === 'log') {
      if (Array.isArray(chartData?.sizeExtent)) {
        const [dataMin, dataMax] = chartData.sizeExtent
        const logMin = Math.log10(dataMin)
        const logMax = Math.log10(dataMax)
        const logDomain = logMax - logMin
        return val => {
          const raw = val[2]
          const logVal = Math.log10(raw)
          if (logDomain === 0) return sizeMin
          const t = (logVal - logMin) / logDomain
          return sizeMin + t * (sizeMax - sizeMin)
        }
      }
      return () => sizeMin
    }

    if (symbolSize === 'outlier') {
      if (chartData?.sizeStats) {
        const { mean, std } = chartData.sizeStats
        const maxZ = 3
        return val => {
          const raw = val[2]
          const z = std ? (raw - mean) / std : 0
          const t = Math.min(Math.abs(z) / maxZ, 1)
          return sizeMin + t * (sizeMax - sizeMin)
        }
      }
      return () => sizeMin
    }

    if (symbolSize === 'exp') {
      if (Array.isArray(chartData?.sizeExtent)) {
        const [dataMin, dataMax] = chartData.sizeExtent
        const domain = dataMax - dataMin
        return val => {
          const raw = val[2]
          if (domain === 0) return sizeMin
          const t = (raw - dataMin) / domain
          const tExp = (Math.exp(t) - 1) / (Math.E - 1)
          return sizeMin + tExp * (sizeMax - sizeMin)
        }
      }
      return () => sizeMin
    }

    return symbolSize
  }, [chartData?.sizeExtent, chartData?.sizeStats, symbolSize, sizeMin, sizeMax])

  if (isLoading) return null

  return (
    <ComponentScatter
      data={chartData.series}
      valueFormat={xFormatter}
      categoryFormat={yFormatter}
      symbolSize={_symbolSize}
      {...seriesProps}
    />
  )
}

DenaliComponentScatter.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  xAccessor: PropTypes.string,
  yAccessor: PropTypes.string,
  sizeAccessor: PropTypes.string,
  labelAccessor: PropTypes.string,
  symbolSize: PropTypes.oneOfType([
    PropTypes.oneOf(['linear', 'log', 'outlier', 'exp']),
    PropTypes.number
  ]),
  sizeMin: PropTypes.number,
  sizeMax: PropTypes.number,
  xFormat: PropTypes.string,
  yFormat: PropTypes.string,
  palette: PropTypes.arrayOf(PropTypes.string)
}

DenaliComponentScatter.defaultProps = {
  xFormat: 'marketValue',
  yFormat: 'number',
  height: 300
}

export default DenaliComponentScatter
